import { CommonModule } from '@angular/common'
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router, RouterLink } from '@angular/router'

import { BaseComponent } from '../../shared'
import { PurchasingDocument, PurchasingOrder, PurchasingOrderLineItem, PurchasingReceipt } from '../../shared/data-models'
import { FeatureType } from '../../shared/enums'

interface VendorOption {
    vendorId: number
    vendorName: string
}

interface EquipmentOption {
    equipment: string
    description: string
}

interface SkillCategoryOption {
    code: string
    name: string
}

interface EmCostTypeOption {
    costType: string
    description: string
}

export interface PurchasingWorkOrderItemOption {
    item: number
    description: string
    costCode?: string
}

export interface PurchasingWorkOrderOption {
    source: 'Vista' | 'WebApps'
    workOrderId?: string
    workOrder: string
    description: string
    equipment: string
    items: PurchasingWorkOrderItemOption[]
}

interface PurchasingEquipmentContext {
    equipment: string
    job: string
    workOrders: PurchasingWorkOrderOption[]
}

@Component({
    selector: 'app-purchasing-orders',
    templateUrl: './purchasing-orders.component.html',
    styleUrls: ['./purchasing-orders.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
})
export class PurchasingOrdersComponent extends BaseComponent implements OnInit {
    @ViewChild('extractInput') extractInput!: ElementRef<HTMLInputElement>
    @ViewChild('docInput') docInput!: ElementRef<HTMLInputElement>

    public orders: PurchasingOrder[] = []
    public selectedOrder: PurchasingOrder | null = null
    public orderReceipts: PurchasingReceipt[] = []

    public editing = false
    public creating = false
    public editModel: PurchasingOrder | null = null

    public vendors: VendorOption[] = []
    public equipmentOptions: EquipmentOption[] = []
    public workOrders: PurchasingWorkOrderOption[] = []
    public workOrderItems: PurchasingWorkOrderItemOption[] = []
    public skillCategories: SkillCategoryOption[] = []
    public emCostTypes: EmCostTypeOption[] = []

    public selectedWorkOrderKey = ''
    public showCreateWo = false
    public createWoModel = { description: '', systemAffected: '', notes: '' }
    public isCreatingWo = false

    public emailing = false
    public emailModel = { toEmail: '', ccEmail: '', subject: '', message: '' }

    public isProcessingAi = false
    public isSaving = false
    public extractWarning = ''
    private pendingExtractFiles: File[] = []
    private previousWorkOrderItem: number | undefined

    public readonly statuses = ['Draft', 'Issued', 'Acknowledged', 'PartiallyReceived', 'Received', 'Closed', 'Cancelled']

    public get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingPurchaseOrdersEdit])
    }

    public get canReceive(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingReceivingEdit])
    }

    constructor(private router: Router) {
        super()
        this.isInitialLoading = false
    }

    ngOnInit(): void {
        this.loadOrders()

        this.services.data.http.get<VendorOption[]>('/api/PurchasingOrder/GetVendors').subscribe({
            next: result => (this.vendors = result || []),
            error: () => {},
        })

        this.services.data.http.get<EquipmentOption[]>('/api/VpEquipment').subscribe({
            next: result => (this.equipmentOptions = (result || []).sort((a, b) => a.equipment.localeCompare(b.equipment))),
            error: () => {},
        })

        this.services.data.http.get<SkillCategoryOption[]>('/api/PurchasingOrder/GetSkillCategories').subscribe({
            next: result => (this.skillCategories = result || []),
            error: () => {},
        })

        this.services.data.http.get<EmCostTypeOption[]>('/api/PurchasingOrder/GetEmCostTypes').subscribe({
            next: result => (this.emCostTypes = result || []),
            error: () => {},
        })
    }

    private loadOrders(): void {
        this.services.data.http.get<PurchasingOrder[]>('/api/PurchasingOrder').subscribe({
            next: result => (this.orders = result || []),
            error: () => this.services.notification.error('Failed to load purchase orders'),
        })
    }

    public selectOrder(order: PurchasingOrder): void {
        this.editing = false
        this.creating = false
        this.editModel = null
        this.orderReceipts = []
        this.extractWarning = ''
        this.services.data.http.get<PurchasingOrder>(`/api/PurchasingOrder/GetDetail?id=${order.id}`).subscribe({
            next: result => {
                this.selectedOrder = result
                this.loadOrderReceipts(order.id)
            },
            error: () => this.services.notification.error('Failed to load purchase order'),
        })
    }

    private loadOrderReceipts(orderId: string): void {
        this.services.data.http.get<PurchasingReceipt[]>(`/api/PurchasingReceipt/GetByOrder?orderId=${orderId}`).subscribe({
            next: result => (this.orderReceipts = result || []),
            error: () => (this.orderReceipts = []),
        })
    }

    public navigateToReceive(): void {
        if (!this.selectedOrder) return
        this.router.navigate(['/purchasing/receiving'], { queryParams: { orderId: this.selectedOrder.id } })
    }

    public navigateToInvoice(): void {
        if (!this.selectedOrder) return
        this.router.navigate(['/purchasing/invoicing'], { queryParams: { orderId: this.selectedOrder.id } })
    }

    public startNew(): void {
        this.selectedOrder = null
        this.creating = true
        this.editing = true
        this.extractWarning = ''
        this.pendingExtractFiles = []
        this.showCreateWo = false
        this.workOrders = []
        this.workOrderItems = []
        this.selectedWorkOrderKey = ''
        this.editModel = {
            id: '00000000-0000-0000-0000-000000000000',
            status: 'Draft',
            orderType: 'Equipment',
            source: 'Manual',
            lineItems: [],
            documents: [],
        } as unknown as PurchasingOrder
        this.addLine()
    }

    public startEdit(): void {
        if (!this.selectedOrder) return
        this.creating = false
        this.editing = true
        this.extractWarning = ''
        this.pendingExtractFiles = []
        this.showCreateWo = false
        this.editModel = JSON.parse(JSON.stringify(this.selectedOrder))
        this.previousWorkOrderItem = this.editModel?.workOrderItem
        this.selectedWorkOrderKey = this.buildWorkOrderKey(this.editModel)
        this.loadWorkOrdersForEquipment(this.editModel?.equipment, this.editModel?.workOrder, this.editModel?.workOrderId)
    }

    public cancelEdit(): void {
        this.editing = false
        this.creating = false
        this.editModel = null
        this.showCreateWo = false
        this.pendingExtractFiles = []
        this.extractWarning = ''
    }

    public addLine(): void {
        if (!this.editModel) return
        if (!this.editModel.lineItems) this.editModel.lineItems = []
        this.editModel.lineItems.push({
            id: '00000000-0000-0000-0000-000000000000',
            lineNumber: this.editModel.lineItems.length + 1,
            equipment: this.editModel.equipment,
            workOrder: this.editModel.workOrder,
            workOrderItem: this.editModel.workOrderItem,
        } as unknown as PurchasingOrderLineItem)
    }

    public removeLine(index: number): void {
        this.editModel?.lineItems?.splice(index, 1)
        this.recalcTotals()
    }

    public recalcLine(line: PurchasingOrderLineItem): void {
        const qty = line.quantity ?? 0
        const price = line.unitPrice ?? 0
        line.lineTotal = Math.round(qty * price * 100) / 100
        this.recalcTotals()
    }

    public recalcTotals(): void {
        if (!this.editModel) return
        const subtotal = (this.editModel.lineItems || []).reduce((sum, l) => sum + (l.lineTotal ?? 0), 0)
        this.editModel.subtotal = Math.round(subtotal * 100) / 100
        const tax = this.editModel.taxAmount ?? 0
        const freight = this.editModel.freightAmount ?? 0
        this.editModel.total = Math.round((subtotal + tax + freight) * 100) / 100
    }

    public onVendorChange(): void {
        if (!this.editModel) return
        const match = this.vendors.find(v => v.vendorId === Number(this.editModel!.vendor))
        this.editModel.vendorName = match ? match.vendorName : this.editModel.vendorName
    }

    public onEquipmentChange(): void {
        if (!this.editModel) return
        this.editModel.workOrder = undefined as unknown as string
        this.editModel.workOrderId = undefined
        this.editModel.workOrderItem = undefined
        this.selectedWorkOrderKey = ''
        this.workOrderItems = []
        this.showCreateWo = false
        this.stampLinesFromHeader(true)
        this.loadWorkOrdersForEquipment(this.editModel.equipment)
    }

    public onWorkOrderChange(): void {
        if (!this.editModel) return
        const selected = this.workOrders.find(wo => this.workOrderKey(wo) === this.selectedWorkOrderKey)
        if (!selected) {
            this.editModel.workOrder = undefined as unknown as string
            this.editModel.workOrderId = undefined
            this.editModel.workOrderItem = undefined
            this.workOrderItems = []
            return
        }

        this.editModel.workOrder = selected.workOrder
        this.editModel.workOrderId = selected.workOrderId
        this.workOrderItems = selected.items || []
        const defaultItem = this.workOrderItems.find(i => i.item === 1) || this.workOrderItems[0]
        this.editModel.workOrderItem = defaultItem?.item
        this.applySelectedItemCoding(defaultItem)
        this.stampLinesFromHeader(false)
        this.previousWorkOrderItem = this.editModel.workOrderItem
    }

    public isUnlistedWorkOrderItem(workOrderItem: number | undefined): boolean {
        if (workOrderItem == null) return false
        return !this.workOrderItems.some(item => item.item === workOrderItem)
    }

    public onHeaderWorkOrderItemChange(): void {
        if (!this.editModel) return
        const item = this.workOrderItems.find(i => i.item === this.editModel!.workOrderItem)
        this.applySelectedItemCoding(item)
        this.stampLinesFromHeader(false)
        this.previousWorkOrderItem = this.editModel.workOrderItem
    }

    public openCreateWo(): void {
        if (!this.editModel?.equipment) {
            this.services.notification.error('Select equipment first')
            return
        }
        this.createWoModel = {
            description: this.editModel.description || '',
            systemAffected: '',
            notes: '',
        }
        this.showCreateWo = true
    }

    public createWorkOrder(): void {
        if (!this.editModel?.equipment) return
        if (!this.createWoModel.description?.trim()) {
            this.services.notification.error('Work order description is required')
            return
        }
        if (!this.createWoModel.systemAffected) {
            this.services.notification.error('System affected is required')
            return
        }

        this.isCreatingWo = true
        this.services.data.http
            .post<PurchasingWorkOrderOption>('/api/PurchasingOrder/CreateWorkOrder', {
                equipment: this.editModel.equipment,
                description: this.createWoModel.description.trim(),
                systemAffected: this.createWoModel.systemAffected,
                notes: this.createWoModel.notes?.trim() || undefined,
                job: this.editModel.job || undefined,
            })
            .subscribe({
                next: created => {
                    this.isCreatingWo = false
                    this.showCreateWo = false
                    this.workOrders = [created, ...this.workOrders.filter(wo => wo.workOrder !== created.workOrder)]
                    this.selectedWorkOrderKey = this.workOrderKey(created)
                    this.onWorkOrderChange()
                    this.services.notification.success(`Created work order ${created.workOrder}`)
                },
                error: err => {
                    this.isCreatingWo = false
                    this.services.notification.error(
                        typeof err.error === 'string' ? err.error : err.error?.error || 'Failed to create work order'
                    )
                },
            })
    }

    public save(): void {
        if (!this.editModel) return
        this.recalcTotals()
        this.isSaving = true

        const request = this.creating
            ? this.services.data.http.post<PurchasingOrder>('/api/PurchasingOrder', this.editModel)
            : this.services.data.http.put<PurchasingOrder>('/api/PurchasingOrder', this.editModel)

        request.subscribe({
            next: result => {
                const filesToUpload = [...this.pendingExtractFiles]
                this.pendingExtractFiles = []
                this.isSaving = false
                this.services.notification.success('Purchase order saved')
                this.editing = false
                this.creating = false
                this.editModel = null
                this.loadOrders()
                this.selectOrder(result)
                if (filesToUpload.length > 0) {
                    this.uploadDocumentsForOrder(result.id, filesToUpload, 'Receipt')
                }
            },
            error: () => {
                this.isSaving = false
                this.services.notification.error('Failed to save purchase order')
            },
        })
    }

    public deleteOrder(order: PurchasingOrder): void {
        if (!confirm(`Archive purchase order ${order.poNumber || ''}?`)) return
        this.services.data.http.delete(`/api/PurchasingOrder/${order.id}`).subscribe({
            next: () => {
                this.services.notification.success('Purchase order archived')
                if (this.selectedOrder?.id === order.id) this.selectedOrder = null
                this.loadOrders()
            },
            error: () => this.services.notification.error('Failed to archive purchase order'),
        })
    }

    public triggerExtract(): void {
        if (this.selectedOrder && !this.editing) {
            this.startEdit()
        }
        this.extractInput.nativeElement.click()
    }

    public onExtractFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        if (!input.files || input.files.length === 0) return
        const files = Array.from(input.files)
        input.value = ''

        this.isProcessingAi = true
        const formData = new FormData()
        for (const file of files) {
            formData.append('files', file, file.name)
        }

        this.services.data.http.post<PurchasingOrder>('/api/PurchasingOrder/ExtractFromDocument', formData).subscribe({
            next: result => {
                this.isProcessingAi = false
                this.applyExtractedOrder(result, files)
            },
            error: err => {
                this.isProcessingAi = false
                this.services.notification.error(err.error?.error || 'Failed to extract receipt data')
            },
        })
    }

    public triggerDocUpload(): void {
        this.docInput.nativeElement.click()
    }

    public onDocFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        if (!input.files || input.files.length === 0 || !this.selectedOrder) return

        const files = Array.from(input.files)
        input.value = ''
        this.uploadDocumentsForOrder(this.selectedOrder.id, files, 'Other')
    }

    public openEmail(): void {
        if (!this.selectedOrder) return
        this.emailModel = {
            toEmail: '',
            ccEmail: '',
            subject: `Purchase Order ${this.selectedOrder.poNumber || ''}`.trim(),
            message: '',
        }
        this.emailing = true
    }

    public sendEmail(): void {
        if (!this.selectedOrder) return
        const payload = {
            orderId: this.selectedOrder.id,
            toEmail: this.emailModel.toEmail,
            ccEmail: this.emailModel.ccEmail,
            subject: this.emailModel.subject,
            message: this.emailModel.message,
        }
        this.services.data.http.post('/api/PurchasingOrder/EmailToVendor', payload).subscribe({
            next: () => {
                this.services.notification.success('Purchase order emailed to vendor')
                this.emailing = false
                if (this.selectedOrder) this.selectOrder(this.selectedOrder)
            },
            error: err => this.services.notification.error(err.error?.error || 'Failed to email purchase order'),
        })
    }

    public statusBadgeClass(status: string): string {
        switch ((status || '').toLowerCase()) {
            case 'draft':
                return 'bg-secondary'
            case 'issued':
            case 'acknowledged':
                return 'bg-primary'
            case 'partiallyreceived':
                return 'bg-warning text-dark'
            case 'received':
                return 'bg-success'
            case 'closed':
                return 'bg-dark'
            case 'cancelled':
                return 'bg-danger'
            default:
                return 'bg-secondary'
        }
    }

    public lineReceiptClass(line: PurchasingOrderLineItem): string {
        const ordered = line.quantity ?? 0
        const received = line.quantityReceived ?? 0
        if (received === 0) return ''
        if (received < ordered) return 'table-warning'
        if (received > ordered) return 'table-danger'
        return 'table-success'
    }

    public documentUrl(doc: PurchasingDocument): string {
        return `/Resources/Images/${doc.url}`
    }

    public receiptStatusBadgeClass(status: string): string {
        switch ((status || '').toLowerCase()) {
            case 'draft':
                return 'bg-secondary'
            case 'posted':
                return 'bg-success'
            default:
                return 'bg-secondary'
        }
    }

    public workOrderKey(wo: PurchasingWorkOrderOption): string {
        return `${wo.source}|${wo.workOrder}|${wo.workOrderId || ''}`
    }

    public equipmentDisplay(code: string | undefined): string {
        if (!code) return '—'
        const match = this.equipmentOptions.find(e => e.equipment === code)
        return match ? `${match.equipment} - ${match.description}` : code
    }

    public emCostTypeDisplay(code: string | undefined): string {
        if (!code) return ''
        const match = this.emCostTypes.find(ct => ct.costType === code)
        return match ? `${match.costType} - ${match.description}` : code
    }

    private applyExtractedOrder(result: PurchasingOrder, files: File[]): void {
        const extractedLines = result.lineItems || []
        this.extractWarning = result.extractWarning || ''

        if (this.editing && this.editModel) {
            if (!this.editModel.lineItems) this.editModel.lineItems = []
            const keepBlankStarter =
                this.editModel.lineItems.length === 1 &&
                !this.editModel.lineItems[0].description &&
                !this.editModel.lineItems[0].itemCode
            if (keepBlankStarter) this.editModel.lineItems = []

            if (!this.editModel.poNumber && result.poNumber) this.editModel.poNumber = result.poNumber
            if (!this.editModel.vendor && result.vendor) {
                this.editModel.vendor = result.vendor
                this.editModel.vendorName = result.vendorName
            } else if (!this.editModel.vendorName && result.vendorName) {
                this.editModel.vendorName = result.vendorName
            }
            if (!this.editModel.description && result.description) this.editModel.description = result.description
            if (result.taxAmount) this.editModel.taxAmount = (this.editModel.taxAmount ?? 0) + (result.taxAmount ?? 0)
            if (result.freightAmount) this.editModel.freightAmount = (this.editModel.freightAmount ?? 0) + (result.freightAmount ?? 0)

            if (result.poNumber && this.editModel.poNumber && this.normalizePo(result.poNumber) !== this.normalizePo(this.editModel.poNumber)) {
                this.extractWarning = `Document PO ${result.poNumber} does not match this PO ${this.editModel.poNumber}`
            }

            for (const line of extractedLines) {
                line.equipment = this.editModel.equipment
                line.workOrder = this.editModel.workOrder
                line.workOrderItem = this.editModel.workOrderItem
                this.editModel.lineItems.push(line)
            }
            this.renumberLines()
            this.recalcTotals()
            this.pendingExtractFiles.push(...files)

            const existingId = this.editModel.id
            if (existingId && existingId !== '00000000-0000-0000-0000-000000000000') {
                this.uploadDocumentsForOrder(existingId, files, 'Receipt')
                this.pendingExtractFiles = this.pendingExtractFiles.filter(f => !files.includes(f))
            }

            this.services.notification.success('Extracted line items — review and save')
            if (this.extractWarning) this.services.notification.error(this.extractWarning)
            return
        }

        this.selectedOrder = null
        this.creating = true
        this.editing = true
        this.showCreateWo = false
        this.editModel = result
        if (!this.editModel.orderType) this.editModel.orderType = 'Equipment'
        if (!this.editModel.lineItems) this.editModel.lineItems = []
        if (!this.editModel.documents) this.editModel.documents = []
        this.pendingExtractFiles = [...files]
        this.previousWorkOrderItem = this.editModel.workOrderItem
        this.selectedWorkOrderKey = this.buildWorkOrderKey(this.editModel)
        this.stampLinesFromHeader(false)
        this.recalcTotals()
        if (this.editModel.equipment) {
            this.loadWorkOrdersForEquipment(this.editModel.equipment, this.editModel.workOrder, this.editModel.workOrderId)
        }
        this.services.notification.success('Extracted PO data — review and save')
        if (this.extractWarning) this.services.notification.error(this.extractWarning)
    }

    private loadWorkOrdersForEquipment(equipment: string | undefined, selectedWorkOrder?: string, selectedWorkOrderId?: string): void {
        if (!equipment) {
            this.workOrders = []
            this.workOrderItems = []
            return
        }

        this.services.data.http
            .get<PurchasingEquipmentContext>(`/api/PurchasingOrder/GetEquipmentWorkOrders?equipment=${encodeURIComponent(equipment)}`)
            .subscribe({
                next: result => {
                    this.workOrders = result?.workOrders || []
                    if (this.editModel && result?.job && !this.editModel.job) {
                        this.editModel.job = result.job
                    }
                    const selected = this.workOrders.find(
                        wo =>
                            (selectedWorkOrderId && wo.workOrderId === selectedWorkOrderId) ||
                            (!!selectedWorkOrder && wo.workOrder === selectedWorkOrder)
                    )
                    if (selected) {
                        this.selectedWorkOrderKey = this.workOrderKey(selected)
                        this.workOrderItems = selected.items || []
                    } else {
                        this.workOrderItems = []
                    }
                },
                error: () => {
                    this.workOrders = []
                    this.workOrderItems = []
                },
            })
    }

    private stampLinesFromHeader(force: boolean): void {
        if (!this.editModel?.lineItems) return
        for (const line of this.editModel.lineItems) {
            const canStamp = force || !line.workOrderItem || line.workOrderItem === this.previousWorkOrderItem
            if (!canStamp) continue
            line.equipment = this.editModel.equipment
            line.workOrder = this.editModel.workOrder
            line.workOrderItem = this.editModel.workOrderItem
            if (!line.costCode) {
                const item = this.workOrderItems.find(i => i.item === this.editModel!.workOrderItem)
                if (item?.costCode) line.costCode = item.costCode
            }
        }
    }

    private applySelectedItemCoding(item: PurchasingWorkOrderItemOption | undefined): void {
        if (!this.editModel || !item?.costCode) return
        for (const line of this.editModel.lineItems || []) {
            if (!line.costCode || line.workOrderItem === this.previousWorkOrderItem) {
                line.costCode = item.costCode
            }
        }
    }

    private buildWorkOrderKey(order: PurchasingOrder | null | undefined): string {
        if (!order?.workOrder) return ''
        const source = order.workOrderId ? 'WebApps' : 'Vista'
        return `${source}|${order.workOrder}|${order.workOrderId || ''}`
    }

    private renumberLines(): void {
        if (!this.editModel?.lineItems) return
        this.editModel.lineItems.forEach((line, index) => {
            line.lineNumber = index + 1
        })
    }

    private normalizePo(value: string): string {
        return (value || '').replace(/\s+/g, '').toUpperCase()
    }

    private uploadDocumentsForOrder(orderId: string, files: File[], documentType: string): void {
        const formData = new FormData()
        formData.append('id', orderId)
        formData.append('documentType', documentType)
        for (const file of files) {
            formData.append('files', file, file.name)
        }

        this.services.data.http.post<PurchasingDocument[]>('/api/PurchasingOrder/UploadDocuments', formData).subscribe({
            next: result => {
                if (this.selectedOrder?.id === orderId) this.selectedOrder.documents = result || []
                this.services.notification.success('Document uploaded')
            },
            error: () => this.services.notification.error('Failed to upload document'),
        })
    }
}
